import { initialStyleBarState, StyleBarState } from "app/core/components/sidebars/StyleBar"
import { ButtonSelection } from "app/core/components/dashboard/d3/types"
import { Editor } from "@tiptap/core"
import { getButtonType } from "app/core/components/dashboard/d3/button"

export type StyleOption<OptionType extends keyof StyleBarState> = {
  option: OptionType
  value: StyleBarState[OptionType]
}

// applies the style option to the currently selected dashboard buttons
// returns true if a change has been made
export type StyleOptionSetter = <OptionType extends keyof StyleBarState>(
  option: OptionType,
  value: StyleBarState[OptionType],
) => boolean

export const applyStyleBarOption =
  (element?: any, editor?: Editor) =>
  <OptionType extends keyof StyleBarState>(
    attribute: OptionType,
    value: StyleBarState[OptionType],
  ) => {
    // the editor takes priority
    // anything handled by the editor should not be set on the button,
    // as selection-specific and button-wide options would conflict
    if (editor) {
      switch (attribute) {
        case "focus":
          if (value) {
            // manually trigger editor focus (used for fontSize)
            editor.commands.focus()
          }
          return
        case "bold":
          if (value) {
            editor.chain().focus().setBold().run()
          } else {
            editor.chain().focus().unsetBold().run()
          }
          return
        case "italics":
          if (value) {
            editor.chain().focus().setItalic().run()
          } else {
            editor.chain().focus().unsetItalic().run()
          }
          return
        case "underline":
          if (value) {
            editor.chain().focus().setTextUnderline(true).run()
          } else {
            editor.chain().focus().unsetTextUnderline().run()
          }
          return
        case "textAlign":
          editor
            .chain()
            .setTextAlign(value as StyleBarState["textAlign"])
            .focus()
            .run()
          return
        case "fontFamily":
          if (value) {
            editor
              .chain()
              .focus()
              .setFontFamily(value as string)
              .run()
          }
          return
        case "color":
          editor
            .chain()
            .focus()
            .setColor(value as string)
            .run()
          return
        case "fontSize": {
          const fontSize = `${Number(value) / 20}em`
          // do not use focus() here, else the Firefox range slider will get stuck
          editor.chain().setFontSize(fontSize).run()
          return
        }
        case "verticalAlign": {
          const el = editor.options.element instanceof HTMLElement ? editor.options.element : null
          el?.firstElementChild?.classList?.toggle("align-self-start", value === "top")
          el?.firstElementChild?.classList?.toggle("align-self-end", value === "bottom")
          editor.chain().focus().run()
        }
        // this option does apply button-wide, so don't return here
      }
    }

    if (element) {
      const htmlContainer = element.selectAll(".dashboard-button-html-container")
      switch (attribute) {
        case "buttonType":
          switch (value) {
            case "rect": {
              element.selectAll(".dashboard-button-rect").style("display", "inline")
              element.selectAll(".dashboard-button-ellipse").style("display", "none")
              element.selectAll(".dashboard-button-image").style("display", "none")
              if (htmlContainer?.size()) {
                htmlContainer.style("display", "inline")
              }
              return
            }
            case "ellipse": {
              element.selectAll(".dashboard-button-rect").style("display", "none")
              element.selectAll(".dashboard-button-ellipse").style("display", "inline")
              element.selectAll(".dashboard-button-image").style("display", "none")
              if (htmlContainer?.size()) {
                htmlContainer.style("display", "inline")
              }
              return
            }
            case "image": {
              element.selectAll(".dashboard-button-rect").style("display", "none")
              element.selectAll(".dashboard-button-ellipse").style("display", "none")
              element.selectAll(".dashboard-button-image").style("display", "inline")
              if (htmlContainer?.size()) {
                htmlContainer.style("display", "none")
              }
              return
            }
            default: {
              element.selectAll(".dashboard-button-rect").style("display", "none")
              element.selectAll(".dashboard-button-ellipse").style("display", "none")
              element.selectAll(".dashboard-button-image").style("display", "none")
              if (htmlContainer?.size()) {
                htmlContainer.style("display", "inline")
              }
              return
            }
          }
        case "backgroundColor":
          element.selectAll(".dashboard-button-main").style("fill", value)
          return
        case "opacity":
          element.selectAll(".dashboard-button").style("opacity", value + "%")
          return
        case "borderWidth":
          element.selectAll(".dashboard-button-main").style("stroke-width", value)
          return
        case "borderColor":
          element.selectAll(".dashboard-button-main").style("stroke", value)
          return
        case "borderRadius":
          element.selectAll(".dashboard-button-rect").attr("rx", value).attr("ry", value)
          return
        case "borderStyle":
          switch (value) {
            case "dashed":
              element.selectAll(".dashboard-button-main").style("stroke-dasharray", "10,5")
              return
            case "dotted":
              element.selectAll(".dashboard-button-main").style("stroke-dasharray", "5,5")
              return
            default:
              element.selectAll(".dashboard-button-main").style("stroke-dasharray", "none")
              return
          }
        case "shadowOffset":
          element
            .selectAll(".dashboard-button-shadow")
            .attr("offset", value)
            .style("transform", `translate(${Number(value)}px, ${Number(value)}px)`)
          return
        case "shadowColor":
          element.selectAll(".dashboard-button-shadow").style("fill", value)
          return
        case "color": {
          const paragraphElement = element.selectAll(".dashboard-button-html-container p")
          if (paragraphElement?.size()) {
            paragraphElement.style("color", value)
          }
          const spanElement = element.selectAll(".dashboard-button-html-container span")
          if (spanElement?.size()) {
            spanElement.style("color", value)
          }
          return
        }
        case "verticalAlign": {
          const htmlElement = element.selectAll(".dashboard-button-html")
          if (htmlElement?.size()) {
            htmlElement.classed("align-self-start", value === "top")
            htmlElement.classed("align-self-end", value === "bottom")
          }
          return
        }
      }
    }
  }

export const styleBarOptionGetter =
  (element?: ButtonSelection, editor?: Editor) =>
  <OptionType extends keyof StyleBarState>(attribute: OptionType): any => {
    if (element) {
      switch (attribute) {
        case "buttonType":
          if (getStyle(element, ".dashboard-button-rect", "display") === "inline") {
            return "rect" as StyleBarState[OptionType]
          }
          if (getStyle(element, ".dashboard-button-ellipse", "display") === "inline") {
            return "ellipse" as StyleBarState[OptionType]
          }
          if (getStyle(element, ".dashboard-button-image", "display") === "inline") {
            return "image" as StyleBarState[OptionType]
          }
          if (
            getStyle(element, ".dashboard-button-rect", "display") === "none" &&
            getStyle(element, ".dashboard-button-ellipse", "display") === "none" &&
            getStyle(element, ".dashboard-button-image", "display") === "none"
          ) {
            return "text" as StyleBarState[OptionType]
          }
          return undefined
        case "backgroundColor":
          return convertRGBToHex(
            getStyle(element, ".dashboard-button-main", "fill"),
          ) as StyleBarState[OptionType]
        case "opacity":
          return (Number(getStyle(element, ".dashboard-button-main", "opacity")) *
            100) as StyleBarState[OptionType]
        case "borderWidth":
          return Number(
            getStyle(element, ".dashboard-button-main", "strokeWidth").replace(/[^0-9]/g, ""),
          ) as StyleBarState[OptionType]
        case "borderColor":
          return convertRGBToHex(
            getStyle(element, ".dashboard-button-main", "stroke"),
          ) as StyleBarState[OptionType]
        case "borderRadius":
          return Number(
            element
              .select(".dashboard-button-main")
              .attr("rx")
              ?.replace(/[^0-9]/g, "") || 0,
          ) as StyleBarState[OptionType]
        case "borderStyle":
          switch (getStyle(element, ".dashboard-button-main", "strokeDasharray")) {
            case "10px, 5px":
              return "dashed" as StyleBarState[OptionType]
            case "5px, 5px":
              return "dotted" as StyleBarState[OptionType]
            default:
              return "solid" as StyleBarState[OptionType]
          }
        case "shadowOffset":
          return (
            element.select(".dashboard-button-shadow")?.attr("offset") ||
            (0 as StyleBarState[OptionType])
          )
        case "shadowColor":
          return convertRGBToHex(
            getStyle(element, ".dashboard-button-shadow", "fill"),
          ) as StyleBarState[OptionType]
        case "verticalAlign":
          const htmlElement = element.selectAll(".dashboard-button-html")
          if (!htmlElement?.size()) {
            return "middle"
          }
          if (htmlElement.classed("align-self-start")) {
            return "top"
          }
          if (htmlElement.classed("align-self-end")) {
            return "bottom"
          }
          return "middle"
      }
    }
    if (editor) {
      switch (attribute) {
        case "textAlign": {
          if (editor?.isActive({ textAlign: "left" })) {
            return "left"
          }
          if (editor?.isActive({ textAlign: "right" })) {
            return "right"
          }
          return "center"
        }
        case "fontFamily": {
          const textStyle = editor?.getAttributes("textStyle")
          return textStyle?.fontFamily ?? initialStyleBarState.fontFamily
        }
        case "fontSize": {
          const textStyle = editor?.getAttributes("textStyle")
          let fontSize = textStyle?.fontSize
          if (fontSize) {
            fontSize = (Number(fontSize.toString().replace("em", "")) * 20).toFixed()
          } else {
            fontSize = initialStyleBarState.fontSize
          }
          if (!fontSize || Number.isNaN(fontSize)) {
            fontSize = 20
          }
          return fontSize
        }
        case "color": {
          const textStyle = editor?.getAttributes("textStyle")
          const color = textStyle?.color ?? initialStyleBarState.color
          return convertRGBToHex(color) || "#000000"
        }
        case "bold": {
          return editor?.isActive("bold")
        }
        case "italics": {
          return editor?.isActive("italic")
        }
        case "underline": {
          const textStyle = editor?.getAttributes("textStyle")
          return textStyle?.textUnderline
        }
      }
      return initialStyleBarState[attribute]
    }
  }

export const convertRGBToHex = (possibleRGBColorValue?: string) => {
  const regexMatch = possibleRGBColorValue?.match(/rgb\(([0-9]*),\s*([0-9]*),\s*([0-9]*)\)/)
  if (!regexMatch?.length || regexMatch?.length <= 3) {
    return possibleRGBColorValue
  }
  return (
    "#" +
    (
      (1 << 24) +
      (Number(regexMatch[1]) << 16) +
      (Number(regexMatch[2]) << 8) +
      Number(regexMatch[3])
    )
      .toString(16)
      .slice(1)
  )
}

export const getStyle = (element: ButtonSelection, selector: string, styleAttribute: string) => {
  const node = element.select<SVGGElement>(selector).node()
  return node ? getComputedStyle(node)[styleAttribute] : initialStyleBarState[styleAttribute]
}

export const styleBarState = (editor: Editor, button?: ButtonSelection): StyleBarState => {
  if (!button) {
    return { ...initialStyleBarState, enabledOptions: [] }
  }

  const newState = Object.keys(initialStyleBarState).reduce(
    <OptionType extends keyof StyleBarState>(accumulator, key: OptionType) => {
      return { ...accumulator, [key]: styleBarOptionGetter(button, editor)(key) }
    },
    {},
  ) as Partial<StyleBarState>

  let enabledOptions: ("text" | "object" | "backgroundColor" | "border" | "shadow")[] = ["object"]
  if (button) {
    const buttonType = getButtonType(button)
    switch (buttonType) {
      case "ellipse": // fallthrough
      case "rect":
        enabledOptions = ["object", "backgroundColor", "text", "border", "shadow"]
        break
      case "image":
        enabledOptions = ["object"]
        break
      case "text":
        enabledOptions = ["text"]
    }
  }

  return { ...initialStyleBarState, ...newState, enabledOptions }
}
